import { Component } from 'react';
import classNames from 'classnames';
import { DrawControl } from 'hcmaps-leaflet';
import SVGInline from 'react-svg-inline';

import {
  buildDrawControlButtons,
  isLayerCircle,
  isCircleRadiusChanged
} from 'legacy/utils/maps';
import { noop } from 'legacy/utils/utils';

import LeafletCircleEditTooltip from 'legacy/components/LeafletCircleEditTooltip';
import DrawControlButton from 'legacy/components/DrawControlButton';

import homeIcon from 'legacy/assets/svg-inline/home.svg';

import { MapDrawControls as theme } from 'legacy/css-modules';

const CIRCLE_EDIT_TOOLTIP_TIMEOUT = 1000;

class MapDrawControls extends Component {
  state = {
    circleEditTooltipVisible: false,
    circleEditRadius: null,
    circleEditTooltipCoords: {
      top: 0,
      left: 0
    }
  };

  drawControlButtons = [];
  editedGeoJSON = null;

  circleEditTooltipHideTimeout = null;

  componentWillMount() {
    this.drawControlButtons = buildDrawControlButtons(
      theme,
      this.props.activeControlType
    );
  }

  componentWillUpdate(nextProps) {
    if (this.props.activeControlType !== nextProps.activeControlType) {
      this.drawControlButtons = buildDrawControlButtons(
        theme,
        nextProps.activeControlType
      );
    }
  }

  componentWillUnmount() {
    if (this.circleEditTooltipHideTimeout) {
      clearTimeout(this.circleEditTooltipHideTimeout);
    }
  }

  updateCircleEditTooltip = (layerRadius, e) => {
    const editHandleMarkers = document
      .querySelectorAll('.map-draw--edit')[0]
      .querySelectorAll('.leaflet-marker-draggable');
    const editHandleMarkerRadiusCoords =
      editHandleMarkers[editHandleMarkers.length - 1].getBoundingClientRect();
    clearTimeout(this.circleEditTooltipHideTimeout);
    this.setState(
      {
        circleEditRadius: layerRadius,
        circleEditTooltipVisible: true,
        circleEditTooltipCoords: {
          top: editHandleMarkerRadiusCoords.top,
          left: editHandleMarkerRadiusCoords.left + 20
        }
      },
      () => {
        this.circleEditTooltipHideTimeout = setTimeout(
          this.hideCircleEditTooltip,
          CIRCLE_EDIT_TOOLTIP_TIMEOUT
        );
      }
    );
  };

  hideCircleEditTooltip = () => {
    this.setState({
      circleEditRadius: null,
      circleEditTooltipVisible: false,
      circleEditTooltipCoords: {
        top: 0,
        left: 0
      }
    });
  };

  handleEditPolygon = (e) => {
    const { layer } = e;
    const { editPolygon } = this.props;
    if (isLayerCircle(layer)) {
      const newRadius = layer.getRadius();
      if (
        isCircleRadiusChanged(layer) &&
        this.state.circleEditRadius !== newRadius
      ) {
        this.updateCircleEditTooltip(newRadius, e);
      }
    }
    editPolygon(layer);
  };

  updatePolygon = (geoJSON) => {
    this.props.updatePolygon(geoJSON);
  };

  enableLocationMode = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const { enableLocationMode, showLoginPrompt, showLoginPromptForDraw } =
      this.props;
    if (showLoginPromptForDraw) {
      return showLoginPrompt();
    } else {
      return enableLocationMode();
    }
  };

  enableDrawMode = (e) => {
    const { enableDrawMode, showLoginPrompt, showLoginPromptForDraw } =
      this.props;
    if (showLoginPromptForDraw) {
      return showLoginPrompt();
    } else {
      return enableDrawMode(e);
    }
  };

  enableEditMode = (e) => {
    const { enableEditMode, showLoginPrompt, showLoginPromptForDraw } =
      this.props;
    if (showLoginPromptForDraw) {
      return showLoginPrompt();
    } else {
      return enableEditMode(e);
    }
  };

  enableDeleteMode = (e) => {
    const { enableDeleteMode, showLoginPrompt, showLoginPromptForDraw } =
      this.props;
    if (showLoginPromptForDraw) {
      return showLoginPrompt();
    } else {
      return enableDeleteMode(e);
    }
  };

  render() {
    const {
      polygons,
      isVisible,
      addPolygon,
      subjectLocationChangable,
      disableSelectionMode,
      activeControlType,
      locationControlOnly
    } = this.props;
    const {
      circleEditRadius,
      circleEditTooltipVisible,
      circleEditTooltipCoords
    } = this.state;
    return [
      <LeafletCircleEditTooltip
        key="circle-edit-tooltip"
        radius={circleEditRadius}
        visible={circleEditTooltipVisible}
        coords={circleEditTooltipCoords}
      />,
      <div key="draw-controls">
        <div
          className={classNames(
            theme.Component,
            theme[`active-button--${activeControlType}`],
            {
              [theme.hidden]: !isVisible,
              [theme['disabled-button--delete']]: !polygons || !polygons.length
            }
          )}
        >
          {subjectLocationChangable && (
            <DrawControlButton
              type="subject"
              className={theme.LocationControl}
              Icon={() => (
                <SVGInline
                  className={classNames(theme.LocationControlIcon, {
                    [theme.active]: activeControlType === 'subject'
                  })}
                  svg={homeIcon}
                />
              )}
              title="Location"
              isActive={false}
              onClick={this.enableLocationMode}
            />
          )}
          {!locationControlOnly && (
            <DrawControl
              drawControlButtons={this.drawControlButtons}
              editControl
              deleteControl
              addPolygon={addPolygon}
              updatePolygon={this.updatePolygon}
              editPolygon={this.handleEditPolygon}
              enableDrawMode={this.enableDrawMode}
              enableEditMode={this.enableEditMode}
              enableDeleteMode={this.enableDeleteMode}
              disableSelectionMode={disableSelectionMode}
              drawOptions={{
                circle: {
                  shapeOptions: {
                    className: 'shape-being-drawn',
                    color: '#008924',
                    opacity: 1,
                    fillColor: '#008924',
                    fillOpacity: 0.1
                  },
                  metric: false,
                  feet: false,
                  precision: {
                    mi: 2,
                    yd: 0,
                    ft: 0
                  }
                },
                rectangle: {
                  shapeOptions: {
                    className: 'shape-being-drawn',
                    color: '#008924',
                    opacity: 1,
                    fillColor: '#008924',
                    fillOpacity: 0.1
                  }
                },
                polygon: {
                  shapeOptions: {
                    className: 'shape-being-drawn',
                    shapeOptions: {
                      color: '#008924',
                      weight: 4,
                      opacity: 0.5,
                      fillOpacity: 0.2
                    }
                  }
                }
              }}
            />
          )}
        </div>
      </div>
    ];
  }
}

MapDrawControls.defaultProps = {
  editPolygon: noop,
  updatePolygon: noop
};

export default MapDrawControls;
